/**
 * **Eins nach dem anderen**: Illustration, Titel, Text, Knöpfe — für den
 * leeren Zustand auf Geschafft und Ruhend (gewünscht 24.09.2026).
 *
 * Die Illustration kommt wie das Bild im Belohnungs-Overlay
 * (`done-moment-pop`, DoneMoment.scss): aus 60 % Größe mit leichtem
 * Nachfedern — nur **ohne Drehung**. Der Rest steigt danach je ein Stück
 * herauf, dicht genug hintereinander, dass es fließt, und weit genug, dass
 * man die Reihenfolge liest. Nach gut einer Sekunde steht alles.
 *
 * Varianten statt Einzelwerte: Die Box schaltet nur zwischen `hidden` und
 * `shown`, jedes Kind kennt seinen eigenen Takt. So spielt dieselbe Abfolge
 * auch nach einem Tabwechsel wieder (EmptyState, `useArrival`).
 *
 * **Nur `opacity` und ein ganzer `transform`** (25.09.2026): `y` und `scale`
 * als Einzelwerte rechnet framer-motion Bild für Bild auf dem Main-Thread. Beim
 * Wechsel des Lebensbereichs ist der belegt, und die Bewegung sprang. Ein ganzer
 * `transform` geht an die Web Animations API und läuft im Compositor weiter.
 * Anfang und Ende tragen dieselben Funktionen, sonst ließe er sich nicht
 * interpolieren.
 *
 * Bewusst ohne DOM-Bezug, wie `arriveMotion`: hier gerechnet, dort eingesetzt.
 */

import type { Variants } from 'framer-motion'

export const REVEAL_ORDER = ['illustration', 'title', 'text', 'actions'] as const
export type RevealStep = (typeof REVEAL_ORDER)[number]

/** Aus `done-moment-pop` / `--reveal-spring` und `--reveal-ease` (DoneMoment.scss). */
const SPRING = [0.34, 1.56, 0.64, 1] as const
const EASE = [0.16, 1, 0.3, 1] as const

/**
 * Wann jeder Schritt beginnt (s). Die Illustration führt; Titel und Text
 * kommen fast zugleich als ein Block (gewünscht 24.09.2026), die Knöpfe danach.
 */
const START: Record<RevealStep, number> = {
  illustration: 0.08,
  title: 0.4,
  text: 0.44,
  actions: 0.56,
}

/**
 * `startAt`: Die Abfolge beginnt erst bei diesem Schritt — er kommt so bald wie sonst die Illustration, alles
 * davor steht schon. Für Teile, die später noch einmal hereinkommen (leerer Zustand in Kurz notiert: die Knöpfe
 * nach „Nein danke“ auf das Angebot der Einführung, #323).
 */
export function revealSequence({ reduced = false, startAt }: { reduced?: boolean; startAt?: RevealStep } = {}): Record<RevealStep, Variants> {
  if (reduced) {
    const still = (transform: string): Variants => ({
      hidden: { opacity: 1, transform },
      shown: { opacity: 1, transform, transition: { duration: 0 } },
    })
    const rest = still('translateY(0px)')
    return { illustration: still('translateY(0px) scale(1)'), title: rest, text: rest, actions: rest }
  }

  const offset = startAt ? START[startAt] - START.illustration : 0
  const at = (step: RevealStep) => Math.max(0, START[step] - offset)

  const follow = (delay: number): Variants => ({
    hidden: { opacity: 0, transform: 'translateY(8px)' },
    shown: {
      opacity: 1,
      transform: 'translateY(0px)',
      transition: { opacity: { duration: 0.4, ease: EASE, delay }, transform: { duration: 0.5, ease: EASE, delay } },
    },
  })

  return {
    illustration: {
      hidden: { opacity: 0, transform: 'translateY(12px) scale(0.6)' },
      shown: {
        opacity: 1,
        transform: 'translateY(0px) scale(1)',
        transition: {
          // Die Deckkraft ist schnell da, die Größe federt nach. Weg und Größe
          // teilen sich jetzt eine Kurve; der Überschwinger kostet den Weg ~1 px.
          opacity: { duration: 0.3, ease: EASE, delay: at('illustration') },
          transform: { duration: 0.7, ease: SPRING, delay: at('illustration') },
        },
      },
    },
    title: follow(at('title')),
    text: follow(at('text')),
    actions: follow(at('actions')),
  }
}
